import { useState } from 'react'
import Header from './components/Header'
import Footer from './components/Footer'
import FileUpload from './components/FileUpload'
import './styles/App.css'
import {
  parseFile,
  jsonToCsv,
  csvToJson,
  downloadFile,
  copyToClipboard,
  batchConvert,
  ConversionOptions
} from './utils/jsoncsvconverter'

interface ParsedData {
  type: 'json' | 'csv'
  data: any
  filename: string
}

function App() {
  const [files, setFiles] = useState<ParsedData[]>([])
  const [processing, setProcessing] = useState(false)
  const [preview, setPreview] = useState<string[][] | null>(null)
  const [targetFormat, setTargetFormat] = useState<'json' | 'csv'>('csv')
  const [options, setOptions] = useState<ConversionOptions>({
    flattenNested: false,
    inferTypes: true,
    delimiter: ','
  })
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [allFields, setAllFields] = useState<string[]>([])
  const [copySuccess, setCopySuccess] = useState(false)

  const handleFileSelect = async (selectedFile: File) => {
    setProcessing(true)
    try {
      const parsed = await parseFile(selectedFile)
      const newFile: ParsedData = {
        type: parsed.type,
        data: parsed.data,
        filename: selectedFile.name
      }
      setFiles([newFile]) // Single file mode

      // Extract fields for selection
      if (parsed.type === 'json' && Array.isArray(parsed.data)) {
        const fields = new Set<string>()
        parsed.data.forEach((item: any) => {
          Object.keys(item).forEach(key => fields.add(key))
        })
        setAllFields(Array.from(fields))
        setSelectedFields([])
        setTargetFormat('csv')
      } else {
        setAllFields([])
        setSelectedFields([])
        setTargetFormat('json')
      }

      setPreview(null)
    } catch (error) {
      console.error('Parse error:', error)
      alert((error as Error).message)
    } finally {
      setProcessing(false)
    }
  }

  const handlePreview = async () => {
    if (files.length === 0) return

    setProcessing(true)
    try {
      const file = files[0]
      let result

      if (file.type === 'json') {
        const opts: ConversionOptions = {
          ...options,
          selectedFields: selectedFields.length > 0 ? selectedFields : undefined
        }
        result = jsonToCsv(file.data, opts)
      } else {
        result = await csvToJson(file.data, options)
      }

      setPreview(result.preview)
    } catch (error) {
      console.error('Preview error:', error)
      alert((error as Error).message)
    } finally {
      setProcessing(false)
    }
  }

  const handleConvert = async () => {
    if (files.length === 0) return

    setProcessing(true)
    try {
      const file = files[0]
      let convertedData: string
      let mimeType: string
      let extension: string

      if (file.type === 'json') {
        const opts: ConversionOptions = {
          ...options,
          selectedFields: selectedFields.length > 0 ? selectedFields : undefined
        }
        const result = jsonToCsv(file.data, opts)
        convertedData = result.data as string
        mimeType = 'text/csv'
        extension = 'csv'
      } else {
        const result = await csvToJson(file.data, options)
        convertedData = JSON.stringify(result.data, null, 2)
        mimeType = 'application/json'
        extension = 'json'
      }

      const originalName = file.filename.replace(/\.[^.]+$/, '')
      const filename = `${originalName}.${extension}`

      downloadFile(convertedData, filename, mimeType)
      alert(`File converted successfully: ${filename}`)
    } catch (error) {
      console.error('Conversion error:', error)
      alert((error as Error).message)
    } finally {
      setProcessing(false)
    }
  }

  const handleCopyToClipboard = async () => {
    if (files.length === 0) return

    setProcessing(true)
    try {
      const file = files[0]
      let convertedData: string

      if (file.type === 'json') {
        const opts: ConversionOptions = {
          ...options,
          selectedFields: selectedFields.length > 0 ? selectedFields : undefined
        }
        const result = jsonToCsv(file.data, opts)
        convertedData = result.data as string
      } else {
        const result = await csvToJson(file.data, options)
        convertedData = JSON.stringify(result.data, null, 2)
      }

      await copyToClipboard(convertedData)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (error) {
      console.error('Copy error:', error)
      alert((error as Error).message)
    } finally {
      setProcessing(false)
    }
  }

  const handleBatchConvert = async (batchFiles: FileList) => {
    setProcessing(true)
    try {
      const filesArray = Array.from(batchFiles)
      const results = await batchConvert(filesArray, targetFormat, options)

      results.forEach(result => {
        if (result.error) {
          console.error(`Error converting ${result.filename}:`, result.error)
        } else {
          const mimeType = targetFormat === 'json' ? 'application/json' : 'text/csv'
          downloadFile(result.data, result.filename, mimeType)
        }
      })

      const successCount = results.filter(r => !r.error).length
      alert(`Batch conversion complete: ${successCount}/${results.length} files converted`)
    } catch (error) {
      console.error('Batch conversion error:', error)
      alert((error as Error).message)
    } finally {
      setProcessing(false)
    }
  }

  const toggleFieldSelection = (field: string) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    )
  }

  return (
    <div className="app">
      <Header
        title="JSON ⇄ CSV Converter"
        description="Convert between JSON and CSV formats with field selection and type inference"
      />

      <main className="main-content">
        <div className="container">
          <FileUpload
            onFileSelect={handleFileSelect}
            accept=".json,.csv,.txt"
            maxSize={50 * 1024 * 1024} // 50MB
          />

          {files.length > 0 && (
            <div className="file-info">
              <h3>File Information</h3>
              <p><strong>Name:</strong> {files[0].filename}</p>
              <p><strong>Type:</strong> {files[0].type.toUpperCase()}</p>
              <p><strong>Target Format:</strong> {files[0].type === 'json' ? 'CSV' : 'JSON'}</p>

              {/* Options */}
              <div className="options-section" style={{ marginTop: '1rem' }}>
                <h4>Conversion Options</h4>

                {files[0].type === 'json' && (
                  <label style={{ display: 'block', margin: '0.5rem 0' }}>
                    <input
                      type="checkbox"
                      checked={options.flattenNested || false}
                      onChange={(e) => setOptions({ ...options, flattenNested: e.target.checked })}
                    />
                    {' '}Flatten nested JSON objects
                  </label>
                )}

                {files[0].type === 'csv' && (
                  <label style={{ display: 'block', margin: '0.5rem 0' }}>
                    <input
                      type="checkbox"
                      checked={options.inferTypes || false}
                      onChange={(e) => setOptions({ ...options, inferTypes: e.target.checked })}
                    />
                    {' '}Infer data types
                  </label>
                )}

                <label style={{ display: 'block', margin: '0.5rem 0' }}>
                  Delimiter:
                  <select
                    value={options.delimiter}
                    onChange={(e) => setOptions({ ...options, delimiter: e.target.value })}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </label>
              </div>

              {/* Field Selection for JSON to CSV */}
              {files[0].type === 'json' && allFields.length > 0 && (
                <div className="field-selection" style={{ marginTop: '1rem' }}>
                  <h4>Select Fields (optional - all fields by default)</h4>
                  <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd', padding: '0.5rem' }}>
                    {allFields.map(field => (
                      <label key={field} style={{ display: 'block', margin: '0.25rem 0' }}>
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field)}
                          onChange={() => toggleFieldSelection(field)}
                        />
                        {' '}{field}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              {preview && (
                <div className="preview-section" style={{ marginTop: '1rem' }}>
                  <h4>Preview (First 10 rows)</h4>
                  <div style={{ overflow: 'auto', maxHeight: '300px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          {preview[0].map((header, i) => (
                            <th key={i} style={{ border: '1px solid #ddd', padding: '0.5rem', background: '#f0f0f0' }}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.slice(1).map((row, i) => (
                          <tr key={i}>
                            {row.map((cell, j) => (
                              <td key={j} style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handlePreview}
                  disabled={processing}
                  className="btn-secondary"
                >
                  {processing ? 'Loading...' : 'Preview'}
                </button>

                <button
                  onClick={handleConvert}
                  disabled={processing}
                  className="btn-primary"
                >
                  {processing ? 'Converting...' : 'Convert & Download'}
                </button>

                <button
                  onClick={handleCopyToClipboard}
                  disabled={processing}
                  className="btn-secondary"
                >
                  {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                </button>
              </div>
            </div>
          )}

          {/* Batch Conversion */}
          <div className="batch-conversion" style={{ marginTop: '2rem', padding: '1rem', border: '2px dashed #ddd', borderRadius: '8px' }}>
            <h3>Batch Conversion</h3>
            <p>Select multiple files to convert at once</p>

            <label style={{ display: 'block', margin: '1rem 0' }}>
              Convert to:
              <select
                value={targetFormat}
                onChange={(e) => setTargetFormat(e.target.value as 'json' | 'csv')}
                style={{ marginLeft: '0.5rem' }}
              >
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
            </label>

            <input
              type="file"
              multiple
              accept=".json,.csv,.txt"
              onChange={(e) => e.target.files && handleBatchConvert(e.target.files)}
              disabled={processing}
              style={{ display: 'block', margin: '1rem 0' }}
            />
          </div>

          <div className="privacy-notice">
            <h3>🔒 100% Privacy</h3>
            <p>
              All processing happens in your browser.
              No files are uploaded to any server.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default App
